// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "../services/userStorage";
import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Cloud, Flower2, Leaf, Plus, Sprout, Sun, Trash2, TreeDeciduous, X } from 'lucide-react';
import ScreenLayout from './ScreenLayout';

const STORAGE_KEY = 'prayerGarden';

const prayerTypes = [
    { id: 'praise', label: 'Praise', icon: Sun },
    { id: 'request', label: 'Request', icon: Cloud },
    { id: 'guidance', label: 'Guidance', icon: Leaf },
    { id: 'growth', label: 'Growth', icon: TreeDeciduous }
];

const typeMeta = prayerTypes.reduce((map, type) => {
    map[type.id] = type;
    return map;
}, {});

const LEGACY_DEMO_PRAYER_IDS = new Set(['1', '2', '3']);

const loadPrayers = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (!Array.isArray(saved)) return [];

        const prayers = saved.filter((prayer) => !LEGACY_DEMO_PRAYER_IDS.has(String(prayer?.id)));
        if (prayers.length !== saved.length) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(prayers));
        }
        return prayers;
    } catch {
        return [];
    }
};

const TypeIcon = ({ type, size = 20 }) => {
    const Icon = (typeMeta[type] || typeMeta.praise).icon;
    return <Icon size={size} />;
};

const PrayerGarden = () => {
    const [prayers, setPrayers] = useState(loadPrayers);
    const [filter, setFilter] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [newPrayer, setNewPrayer] = useState('');
    const [newType, setNewType] = useState('praise');

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prayers));
    }, [prayers]);

    const counts = useMemo(() => ({
        all: prayers.length,
        answered: prayers.filter((prayer) => prayer.completed).length,
        active: prayers.filter((prayer) => !prayer.completed).length,
        praise: prayers.filter((prayer) => prayer.type === 'praise').length,
        request: prayers.filter((prayer) => prayer.type === 'request').length,
        guidance: prayers.filter((prayer) => prayer.type === 'guidance').length,
        growth: prayers.filter((prayer) => prayer.type === 'growth').length
    }), [prayers]);

    const visiblePrayers = prayers.filter((prayer) => filter === 'all' || prayer.type === filter);

    const addPrayer = () => {
        if (!newPrayer.trim()) return;
        setPrayers([
            { id: Date.now(), text: newPrayer.trim(), type: newType, timestamp: Date.now(), completed: false },
            ...prayers
        ]);
        setNewPrayer('');
        setNewType('praise');
        setShowForm(false);
    };

    const toggleCompletion = (id) => {
        setPrayers(prayers.map((prayer) =>
            prayer.id === id ? { ...prayer, completed: !prayer.completed } : prayer
        ));
    };

    const deletePrayer = (id) => setPrayers(prayers.filter((prayer) => prayer.id !== id));

    const formatDate = (timestamp) => new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    }).format(new Date(timestamp));

    return (
        <ScreenLayout title="Prayer Garden" icon={Sprout}>
            <section className="garden-hero">
                <div>
                    <p className="shared-kicker"><Sprout size={14} /> Plant &amp; tend</p>
                    <h1>Watch your prayers grow.</h1>
                    <p>Plant each prayer, return to tend it, and celebrate the ones God answers over time.</p>
                </div>
                <button className="garden-add-button" onClick={() => setShowForm(true)}>
                    <Plus size={20} /> Plant a prayer
                </button>
            </section>

            <div className="garden-summary" aria-label="Garden summary">
                <div><Sprout size={19} /><span><strong>{counts.all}</strong>Prayers planted</span></div>
                <div><CheckCircle2 size={19} /><span><strong>{counts.answered}</strong>Answered</span></div>
                <div><Flower2 size={19} /><span><strong>{counts.active}</strong>Still growing</span></div>
            </div>

            <nav className="garden-filters" aria-label="Filter prayers">
                {[['all', 'All', counts.all], ...prayerTypes.map((type) => [type.id, type.label, counts[type.id]])].map(([id, label, count]) => (
                    <button key={id} className={filter === id ? 'is-active' : ''} onClick={() => setFilter(id)}>
                        {label}<span>{count}</span>
                    </button>
                ))}
            </nav>

            <section className="garden-list" aria-live="polite">
                {!visiblePrayers.length ? (
                    <div className="garden-empty">
                        <Sprout size={28} />
                        <h2>This bed is empty</h2>
                        <p>Plant a prayer and your garden will begin to grow.</p>
                    </div>
                ) : visiblePrayers.map((prayer) => (
                    <article className={`garden-card garden-card-${prayer.type} ${prayer.completed ? 'is-answered' : ''}`} key={prayer.id}>
                        <span className="garden-card-icon"><TypeIcon type={prayer.type} size={22} /></span>
                        <div>
                            <div className="garden-card-heading">
                                <h2>{(typeMeta[prayer.type] || typeMeta.praise).label}</h2>
                                <span>{prayer.completed ? 'Answered' : 'Growing'}</span>
                            </div>
                            <p>{prayer.text}</p>
                            <small>{prayer.completed ? 'Answered' : 'Planted'} · {formatDate(prayer.timestamp)}</small>
                            <div className="garden-card-actions">
                                <button onClick={() => toggleCompletion(prayer.id)}>
                                    <CheckCircle2 size={15} /> {prayer.completed ? 'Mark growing' : 'Mark answered'}
                                </button>
                                <button className="garden-card-delete" onClick={() => deletePrayer(prayer.id)} aria-label="Remove prayer">
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        </div>
                    </article>
                ))}
            </section>

            {showForm && (
                <div className="garden-overlay" onClick={() => setShowForm(false)}>
                    <section className="garden-form" onClick={(event) => event.stopPropagation()}>
                        <header>
                            <div><small>Plant a new prayer</small><h2>What is on your heart?</h2></div>
                            <button onClick={() => setShowForm(false)} aria-label="Close form"><X size={20} /></button>
                        </header>
                        <label>
                            Your prayer
                            <textarea
                                autoFocus
                                rows="4"
                                value={newPrayer}
                                onChange={(event) => setNewPrayer(event.target.value)}
                                placeholder="Write your prayer in your own words..."
                            />
                        </label>
                        <span className="garden-form-label">Prayer type</span>
                        <div className="garden-type-options">
                            {prayerTypes.map((type) => (
                                <button
                                    type="button"
                                    key={type.id}
                                    className={`garden-type-option ${newType === type.id ? 'is-selected' : ''}`}
                                    onClick={() => setNewType(type.id)}
                                >
                                    <type.icon size={20} />
                                    <span>{type.label}</span>
                                </button>
                            ))}
                        </div>
                        <button className="garden-save-button" onClick={addPrayer} disabled={!newPrayer.trim()}>
                            <Sprout size={18} /> Plant this prayer
                        </button>
                    </section>
                </div>
            )}
        </ScreenLayout>
    );
};

export default PrayerGarden;
